import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import {
    Phone, Facebook, Youtube, Instagram,
    Search, Menu, X, ChevronDown,
    Calendar, MessageCircle, MapPin, Home, Loader2
} from 'lucide-react';
import { normalizeImageUrl } from '../utils/imageUtils';
import { getSettingMapUrl } from '../utils/branchUtils';
import { useData } from '../context/DataContext';
import { searchServices } from '../services/ServiceService';

const menuItems = [
    { title: <Home size={20} />, href: '/' },
    {
        title: 'GIỚI THIỆU',
        href: '/gioi-thieu/ve-chung-toi',
        children: [
            { title: 'Về Chúng Tôi', href: '/gioi-thieu/ve-chung-toi' },
            { title: 'Sự Kiện Trung Anh Garage', href: '/su-kien' },
            { title: 'Hệ Thống Chi Nhánh', href: '/gioi-thieu/he-thong-chi-nhanh' },
        ],
    },
    {
        title: 'DỊCH VỤ',
        href: '/dich-vu',
        children: [
            { title: 'Bảo Dưỡng & Sửa Chữa Ô Tô', href: '/dich-vu/bao-duong-su-chua' },
            { title: 'Chăm Sóc & Làm Đẹp Ô Tô', href: '/dich-vu/cham-soc-lam-dep' },
            { title: 'Sơn & Phục Hồi Thân Vỏ', href: '/dich-vu/son-phuc-hoi-than-vo' },
        ],
    },
    {
        title: 'BLOG',
        href: '/blog',
        children: [],
    },
    {
        title: 'KHUYẾN MẠI',
        href: '/khuyen-mai',
        children: [],
    },
    { title: 'TUYỂN DỤNG', href: '/tuyen-dung' },
    { title: 'ĐÀO TẠO', href: '/dao-tao' },
    {
        title: 'LIÊN HỆ',
        href: '/lien-he',
        children: [
            { title: 'Liên Hệ', href: '/lien-he' },
            { title: 'Đặt Lịch Hẹn', href: '/dat-lich-hen' },
        ],
    },
];

/* ── Dropdown item ── */
const NavItem = ({ item }) => {
    const [open, setOpen] = useState(false);
    const location = useLocation();

    // Check if current page is active for this menu item
    const isActuallyActive = location.pathname.startsWith(item.href) && (item.href !== '/' || location.pathname === '/');

    const commonLinkStyle = {
        display: 'flex', alignItems: 'center', gap: 4,
        padding: '0 16px', height: '52px', // Matches container height
        fontWeight: 700, fontSize: 13,
        textDecoration: 'none', whiteSpace: 'nowrap', cursor: 'pointer',
        transition: 'all 0.3s ease',
        color: (open || isActuallyActive) ? item.hoverColor || '#ff0000' : item.customColor || '#333',
        background: open ? 'rgba(255,255,255,0.1)' : 'transparent',
    };

    return (
        <li
            style={{ position: 'relative', height: '100%', display: 'flex', alignItems: 'center' }}
            onMouseEnter={() => setOpen(true)}
            onMouseLeave={() => setOpen(false)}
        >
            <Link to={item.href} style={commonLinkStyle}>
                {item.title}
                {item.children && <ChevronDown size={11} strokeWidth={3} style={{ marginLeft: 4, transform: open ? 'rotate(180deg)' : 'rotate(0)', transition: '0.3s' }} />}
            </Link>

            {/* Dropdown menu — smooth transition */}
            {item.children && (
                <ul style={{
                    position: 'absolute', top: '100%', left: 0,
                    background: '#fff', minWidth: 260,
                    listStyle: 'none', margin: 0, padding: 0,
                    boxShadow: '0 10px 30px rgba(0,0,0,0.15)',
                    zIndex: 10000,
                    opacity: open ? 1 : 0,
                    visibility: open ? 'visible' : 'hidden',
                    transform: open ? 'translateY(0)' : 'translateY(15px)',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    pointerEvents: open ? 'auto' : 'none',
                    borderTop: '2px solid #ff0000'
                }}>
                    {item.children.map((c, i) => (
                        <SubNavItem key={i} item={c} />
                    ))}
                </ul>
            )}
        </li>
    );
};

/* ── Dropdown Sub Item (Level 2/3) ── */
const SubNavItem = ({ item }) => {
    const [open, setOpen] = useState(false);
    return (
        <li
            style={{ display: 'block', position: 'relative' }}
            onMouseEnter={() => setOpen(true)}
            onMouseLeave={() => setOpen(false)}
        >
            <Link
                to={item.href}
                style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 20px',
                    textDecoration: 'none', fontSize: 13, fontWeight: 600,
                    borderBottom: '1px solid #f5f5f5',
                    background: open ? '#ff0000' : 'transparent',
                    color: open ? '#fff' : '#444',
                    whiteSpace: 'nowrap',
                    transition: 'all 0.2s ease'
                }}
            >
                {item.title}
                {item.children && <ChevronDown size={11} strokeWidth={3} style={{ transform: 'rotate(-90deg)' }} />}
            </Link>

            {item.children && (
                <ul style={{
                    position: 'absolute', top: 0, left: '100%',
                    background: '#fff', minWidth: 240,
                    listStyle: 'none', margin: 0, padding: 0,
                    boxShadow: '0 10px 30px rgba(0,0,0,0.15)',
                    zIndex: 10001,
                    opacity: open ? 1 : 0,
                    visibility: open ? 'visible' : 'hidden',
                    transform: open ? 'translateX(0)' : 'translateX(5px)',
                    transition: 'all 0.3s ease',
                    pointerEvents: open ? 'auto' : 'none',
                    borderLeft: '2px solid #ff0000',
                    paddingLeft: 5, // Bridge gap
                    marginLeft: -5, // Offset bridge
                }}>
                    <div style={{ background: '#fff', boxShadow: 'inherit' }}>
                        {item.children.map((c, i) => (
                            <li key={i} style={{ display: 'block' }}>
                                <Link
                                    to={c.href}
                                    style={{
                                        display: 'block', padding: '12px 20px',
                                        color: '#444', textDecoration: 'none', fontSize: 13,
                                        borderBottom: '1px solid #f5f5f5',
                                        whiteSpace: 'nowrap',
                                        transition: 'all 0.2s'
                                    }}
                                    onMouseEnter={e => { e.currentTarget.style.background = '#ff0000'; e.currentTarget.style.color = '#fff'; }}
                                    onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#444'; }}
                                >
                                    {c.title}
                                </Link>
                            </li>
                        ))}
                    </div>
                </ul>
            )}
        </li>
    );
};


/* ══════ MAIN HEADER ══════ */
const Header = ({ onOpenBooking }) => {
    const navigate = useNavigate();
    const topBarRef = useRef(null);
    const navBarRef = useRef(null);
    const [isFixed, setIsFixed] = useState(false);
    const [topBarH, setTopBarH] = useState(90);
    const [navH, setNavH] = useState(52);
    const [mobileOpen, setMobileOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const [searchResults, setSearchResults] = useState([]);
    const [showResults, setShowResults] = useState(false);
    const [isSearching, setIsSearching] = useState(false);
    const searchRef = useRef(null);

    const { serviceGroups, postCategories, settings } = useData();
    const [dynamicMenuItems, setDynamicMenuItems] = useState(menuItems);

    useEffect(() => {
        // Optimize: Regenerate menu items from constant menuItems when data is available
        const updated = menuItems.map(menu => {
            // 1. Update SERVICES menu
            if (menu.title === 'DỊCH VỤ' && serviceGroups.length > 0) {
                const servicesMenuRaw = serviceGroups.map(group => ({
                    title: group.name,
                    href: `/dich-vu-nhom/${group.slug}`,
                    children: group.items && group.items.length > 0 ? group.items.map(item => ({
                        title: item.title || item.name,
                        href: `/dich-vu/${item.slug}`
                    })) : undefined
                }));
                return { ...menu, children: servicesMenuRaw };
            }

            // 2. Update BLOG menu
            if (menu.title === 'BLOG' && postCategories.length > 0) {
                const blogCategories = postCategories.filter(c => c.title === 'BLOG' || c.parentName === 'BLOG');
                const blogChildren = blogCategories.map(cat => ({
                    title: cat.name,
                    href: `/blog/danh-muc/${cat._id || cat.id}`
                }));
                if (blogChildren.length > 0) return { ...menu, children: blogChildren };
            }

            // 3. Update PROMOTION menu
            if (menu.title === 'KHUYẾN MẠI' && postCategories.length > 0) {
                const promoCats = postCategories.filter(c => c.title === 'PROMOTION' || c.parentName === 'PROMOTION');
                const promoChildren = promoCats.map(cat => ({
                    title: cat.name,
                    href: `/khuyen-mai/danh-muc/${cat._id || cat.id}`
                }));
                if (promoChildren.length > 0) return { ...menu, children: promoChildren };
            }

            return menu;
        });

        setDynamicMenuItems(updated);
    }, [serviceGroups, postCategories]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (searchRef.current && !searchRef.current.contains(event.target)) {
                setShowResults(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    useEffect(() => {
        const timer = setTimeout(async () => {
            if (searchQuery.trim().length >= 2) {
                setIsSearching(true);
                setShowResults(true);
                const results = await searchServices(searchQuery);
                setSearchResults(results);
                setIsSearching(false);
            } else {
                setSearchResults([]);
                setShowResults(false);
            }
        }, 500);

        return () => clearTimeout(timer);
    }, [searchQuery]);

    const handleSearch = (e) => {
        e.preventDefault();
        if (searchQuery.trim()) {
            // navigate(`/tim-kiem?keyword=${encodeURIComponent(searchQuery)}`);
            setShowResults(true);
        }
    };

    /* measure heights once on mount */
    useEffect(() => {
        if (topBarRef.current) setTopBarH(topBarRef.current.offsetHeight);
        if (navBarRef.current) setNavH(navBarRef.current.offsetHeight);
    }, []);

    /* scroll listener — fix nav when top bar scrolls out */
    useEffect(() => {
        const onScroll = () => {
            // fix = when we've scrolled past the top bar height
            setIsFixed(window.scrollY > topBarH);
        };
        window.addEventListener('scroll', onScroll, { passive: true });
        return () => window.removeEventListener('scroll', onScroll);
    }, [topBarH]);

    return (
        <>
            {/* ══ DESKTOP ══ */}
            <header className="d-none d-lg-block w-full">
                {/* Top Bar: keep original Red/White theme but modernize */}
                <div
                    ref={topBarRef}
                    className="relative z-[10001] transition-all duration-300"
                    style={{
                        background: 'linear-gradient(to right, #000, #ff0000, #000)',
                        borderBottom: '1px solid rgba(255,255,255,0.1)',
                        color: '#fff'
                    }}
                >
                    <div className="container">
                        <div className="flex items-center justify-between py-2">
                            {/* Logo + Text (Original Logic) */}
                            <Link to="/" className="flex items-center gap-3 no-underline shrink-0 py-1">
                                <img
                                    src={settings?.logo ? normalizeImageUrl(settings.logo) : "/assets/logo.png"}
                                    alt={settings?.siteName || "Trung Anh Garage"}
                                    style={{ height: 60, width: "auto", objectFit: "contain" }}
                                    fetchPriority="high"
                                />
                                <div className="leading-tight">
                                    <div style={{
                                        fontSize: 18,
                                        fontWeight: 900,
                                        color: "#fff",
                                        fontFamily: "'Orbitron', sans-serif",
                                        letterSpacing: '1px',
                                        textTransform: 'uppercase'
                                    }}>
                                        {settings?.siteName || "Trung Anh Garage"}
                                    </div>
                                    <div className="text-[12px] italic text-white/70">
                                        Sửa nhanh hôm nay - An tâm ngày mai
                                    </div>
                                </div>
                            </Link>

                            {/* Right side: Social + Phone (Original Logic) */}
                            <div className="flex items-center gap-6">
                                <div className="flex items-center gap-4">
                                    {[
                                        { href: settings?.facebookUrl, icon: <img src="/assets/facebook.png" alt="Facebook" className="w-7 h-7 object-contain invert brightness-200" /> },
                                        { href: settings?.youtubeUrl, icon: <img src="/assets/youtobe.png" alt="Youtube" className="w-10 h-10 object-contain invert brightness-200" /> },
                                        { href: settings?.tiktokUrl, icon: <img src="/assets/tiktok.png" alt="TikTok" className="w-11 h-11 object-contain invert brightness-200" /> },
                                    ].map((s, i) => (
                                        <a key={i} href={s.href} target="_blank" rel="noreferrer" className="flex items-center justify-center transition-all duration-300 transform hover:scale-125 drop-shadow-[0_0_5px_rgba(255,255,255,0.4)] hover:drop-shadow-[0_0_8px_rgba(255,255,255,0.8)]">
                                            {s.icon}
                                        </a>
                                    ))}
                                </div>

                                <a href={`tel:${settings?.hotline}`} className="flex items-center gap-3 no-underline group border-l border-white/20 pl-6 py-1">
                                    <div className="w-[36px] h-[36px] rounded-full bg-[#ff0000] flex items-center justify-center group-hover:bg-red-700 transition-colors shadow-[0_0_15px_rgba(255,0,0,0.5)]">
                                        <Phone size={18} color="#fff" fill="#fff" strokeWidth={0} />
                                    </div>
                                    <div className="leading-tight">
                                        <div className="block text-white text-[18px] font-black">{settings?.hotline}</div>
                                        <div className="text-white/60 text-[11px] uppercase tracking-tighter">Hotline 24/7</div>
                                    </div>
                                </a>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Main Nav Bar: Gradient for Integration */}
                <div
                    ref={navBarRef}
                    className="transition-all duration-500"
                    style={{
                        position: isFixed ? 'fixed' : 'absolute',
                        top: isFixed ? 0 : topBarH,
                        left: 0,
                        right: 0,
                        zIndex: 10000,
                        background: isFixed ? 'rgba(0,0,0,0.95)' : 'linear-gradient(to bottom, #000, rgba(0,0,0,0.3) 70%, transparent)',
                        backdropFilter: isFixed ? 'blur(10px)' : 'none',
                        borderTop: isFixed ? '2px solid #ff0000' : 'none',
                        boxShadow: isFixed ? '0 5px 20px rgba(0,0,0,0.5)' : 'none',
                    }}
                >
                    <div className="container">
                        <div className="flex items-stretch flex-nowrap">
                            {/* Menu (Original Logic) */}
                            <ul className="flex items-stretch justify-start list-none m-0 p-0 flex-grow gap-2">
                                {dynamicMenuItems.map((item, i) => (
                                    <NavItem
                                        key={i}
                                        item={{
                                            ...item,
                                            customColor: '#fff',
                                            hoverColor: '#ff0000'
                                        }}
                                    />
                                ))}
                            </ul>

                            {/* Search (Original Logic) */}
                            <div className="flex items-center shrink-0 py-2 relative" ref={searchRef}>
                                <form onSubmit={handleSearch} className="flex items-center bg-white/90 rounded-full overflow-hidden border border-[#ccc] min-w-[220px] transition-all focus-within:ring-2 focus-within:ring-[#ff0000]/30 shadow-sm">
                                    <input
                                        type="text"
                                        name="keyword"
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                        onFocus={() => searchQuery.trim().length >= 2 && setShowResults(true)}
                                        placeholder="Tìm kiếm dịch vụ...."
                                        className="border-none outline-none px-4 py-2 text-sm flex-grow bg-transparent"
                                        autoComplete="off"
                                    />
                                    <button type="submit" className="bg-none border-none px-3 cursor-pointer text-[#ff0000] flex items-center">
                                        {isSearching ? <Loader2 size={18} className="animate-spin" /> : <Search size={18} strokeWidth={2.5} />}
                                    </button>
                                </form>

                                {/* Search Results Dropdown */}
                                {showResults && (
                                    <div className="absolute top-full right-0 mt-2 w-[350px] bg-white rounded-xl shadow-2xl border border-slate-100 overflow-hidden z-[10002] animate-in fade-in slide-in-from-top-2 duration-300">
                                        <div className="p-3 border-b border-slate-50 bg-slate-50/50 flex items-center justify-between">
                                            <span className="text-[10px] font-mono-tech uppercase tracking-widest text-slate-500">Kết quả tìm kiếm</span>
                                            {isSearching && <Loader2 size={12} className="animate-spin text-red-600" />}
                                        </div>
                                        <div className="max-h-[400px] overflow-y-auto">
                                            {searchResults.length > 0 ? (
                                                searchResults.map((service, index) => (
                                                    <Link
                                                        key={index}
                                                        to={`/dich-vu/${service.slug}`}
                                                        onClick={() => setShowResults(false)}
                                                        className="flex items-center gap-4 p-4 hover:bg-red-50 transition-colors group border-b border-slate-50 last:border-none no-underline"
                                                    >
                                                        <div className="w-12 h-12 rounded-lg bg-slate-100 flex-shrink-0 overflow-hidden border border-slate-200">
                                                            {service.thumbnail ? (
                                                                <img src={normalizeImageUrl(service.thumbnail)} alt={service.title} className="w-full h-full object-cover" />
                                                            ) : (
                                                                <div className="w-full h-full flex items-center justify-center text-slate-400">
                                                                    <Search size={16} />
                                                                </div>
                                                            )}
                                                        </div>
                                                        <div className="flex flex-col gap-0.5">
                                                            <span className="text-sm font-bold text-slate-800 group-hover:text-red-600 transition-colors line-clamp-1 truncate">
                                                                {service.title}
                                                            </span>
                                                            <span className="text-[10px] text-slate-400 uppercase tracking-tight">Chi tiết dịch vụ</span>
                                                        </div>
                                                    </Link>
                                                ))
                                            ) : (
                                                !isSearching && (
                                                    <div className="p-8 text-center flex flex-col items-center gap-3">
                                                        <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center text-slate-400">
                                                            <Search size={20} />
                                                        </div>
                                                        <div className="flex flex-col gap-1">
                                                            <span className="text-sm font-bold text-slate-800">Không tìm thấy kết quả</span>
                                                            <span className="text-xs text-slate-400">Thử tìm kiếm với từ khóa khác</span>
                                                        </div>
                                                    </div>
                                                )
                                            )}
                                        </div>
                                        <div className="p-3 bg-slate-50 text-center">
                                            <span className="text-[9px] uppercase tracking-widest text-slate-400 font-bold">Trung Anh Garage System</span>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
                {/* Spacer (only shown when header is fixed to prevent jump) */}
                {isFixed && <div style={{ height: navH }} />}
            </header>


            {/* ══ MOBILE TOP BAR ══ */}
            <div className="d-lg-none" style={{
                position: 'sticky', top: 0, zIndex: 9999,
                background: '#fff', padding: '10px 16px',
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                borderBottom: '2px solid #ff0000', boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            }}>
                <button onClick={() => setMobileOpen(true)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}>
                    <Menu size={26} color="#333" />
                </button>
                <Link to="/" onClick={() => setMobileOpen(false)} style={{ display: 'flex', alignItems: 'center', gap: 8, textDecoration: 'none' }}>
                    <img
                        src={settings?.logo ? normalizeImageUrl(settings.logo) : "/assets/logo.png"}
                        alt={settings?.siteName || "Logo"}
                        style={{ height: 38, width: 'auto', objectFit: "contain" }}
                        fetchPriority="high"
                    />
                    <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                        <span style={{
                            fontSize: 14,
                            fontWeight: 900,
                            color: "#d40000",
                            fontFamily: "'Orbitron', sans-serif",
                            letterSpacing: '0.5px',
                            textTransform: 'uppercase',
                            lineHeight: '1.1'
                        }}>
                            {settings?.siteName || "Trung Anh Garage"}
                        </span>
                        <span style={{
                            fontSize: 8,
                            fontWeight: 600,
                            color: "#555",
                            textTransform: 'uppercase',
                            lineHeight: '1.1',
                            letterSpacing: '0.2px'
                        }}>
                            Sửa nhanh hôm nay - An tâm ngày mai
                        </span>
                    </div>
                </Link>
                <a href={`tel:${settings?.hotline}`} style={{ color: '#ff0000' }}><Phone size={24} /></a>
            </div>

            {/* Mobile off-canvas overlay */}
            {mobileOpen && (
                <div onClick={() => setMobileOpen(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 10000 }} />
            )}

            {/* Mobile off-canvas drawer */}
            <div className="d-lg-none" style={{
                position: 'fixed', top: 0, left: 0, bottom: 0, width: 300,
                background: '#fff', zIndex: 10001, overflowY: 'auto',
                transform: mobileOpen ? 'translateX(0)' : 'translateX(-100%)',
                transition: 'transform 0.3s ease',
            }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 20px', borderBottom: '2px solid #ff0000' }}>
                    <img src={settings?.logo ? normalizeImageUrl(settings.logo) : "/assets/logo.png"} alt="Logo" style={{ height: 44 }} fetchPriority="high" />
                    <button onClick={() => setMobileOpen(false)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
                        <X size={26} color="#ff0000" />
                    </button>
                </div>
                <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                    {dynamicMenuItems.map((item, i) => (
                        <li key={i} style={{ borderBottom: '1px solid #f0f0f0' }}>
                            <Link to={item.href} onClick={() => !item.children && setMobileOpen(false)} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '13px 20px', color: '#333', textDecoration: 'none', fontWeight: 700, fontSize: 14 }}>
                                {item.title} {item.children && <ChevronDown size={14} />}
                            </Link>
                            {item.children && (
                                <ul style={{ listStyle: 'none', margin: 0, padding: 0, background: '#f9f9f9' }}>
                                    {item.children.map((c, j) => (
                                        <li key={j}>
                                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: c.children ? '1px solid #f0f0f0' : 'none' }}>
                                                <Link
                                                    to={c.href}
                                                    onClick={() => !c.children && setMobileOpen(false)}
                                                    style={{ display: 'block', padding: '10px 32px', color: '#555', textDecoration: 'none', fontSize: 13, flex: 1 }}
                                                >
                                                    {c.title}
                                                </Link>
                                                {c.children && <span style={{ padding: '10px 32px 10px 0', pointerEvents: 'none' }}><ChevronDown size={12} color="#888" /></span>}
                                            </div>
                                            {c.children && (
                                                <ul style={{ listStyle: 'none', margin: 0, padding: 0, background: '#f0f0f0' }}>
                                                    {c.children.map((cc, k) => (
                                                        <li key={k}>
                                                            <Link
                                                                to={cc.href}
                                                                onClick={() => setMobileOpen(false)}
                                                                style={{ display: 'block', padding: '8px 44px', color: 'gray', textDecoration: 'none', fontSize: 13, borderBottom: '1px solid #e0e0e0' }}
                                                            >
                                                                - {cc.title}
                                                            </Link>
                                                        </li>
                                                    ))}
                                                </ul>
                                            )}
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </li>
                    ))}
                </ul>
            </div>

            {/* ══ MOBILE BOTTOM STICKY BAR (only here, not in Footer) ══ */}
            <div className="d-lg-none" style={{ position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 9998, background: '#fff', borderTop: '2px solid #ff0000', boxShadow: '0 -2px 10px rgba(0,0,0,0.1)' }}>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    {[
                        { icon: <Calendar size={20} />, label: 'Đặt lịch', href: '/dat-lich-hen' },
                        { icon: <Phone size={20} />, label: settings?.hotline, href: `tel:${settings?.hotline}` },
                        { icon: <MessageCircle size={20} />, label: 'Zalo', href: settings?.zaloUrl },
                        { icon: <MapPin size={20} />, label: 'Chỉ đường', href: getSettingMapUrl(settings?.address) },
                    ].map((item, i) => {
                        const s = { flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '8px 4px', gap: 3, cursor: 'pointer', background: 'none', border: i < 3 ? 'none' : 'none', borderRight: i < 3 ? '1px solid #eee' : 'none', textDecoration: 'none' };
                        const inner = (<><span style={{ color: '#ff0000' }}>{item.icon}</span><span style={{ fontSize: 10, fontWeight: 700, color: '#333' }}>{item.label}</span></>);

                        if (item.href === '/dat-lich-hen') {
                            return <Link key={i} to={item.href} style={s} onClick={() => setMobileOpen(false)}>{inner}</Link>;
                        }

                        return item.href
                            ? <a key={i} href={item.href} target={item.href && item.href.startsWith('http') ? '_blank' : undefined} rel="noreferrer" style={s}>{inner}</a>
                            : <button key={i} onClick={item.action} style={s}>{inner}</button>;
                    })}
                </div>
            </div>
        </>
    );
};

export default Header;



